/**
 * GuardaDaAbaIsolada — a aba que viu o portal de um paciente volta a ser do admin.
 *
 * Incidente 24/set (Matthew): ele abriu "Portal do paciente" > "Abrir em nova
 * aba", e depois usou ESSA MESMA aba para trabalhar. A marca de aba isolada
 * (SESSAO_ISOLADA_KEY, lib/supabaseClient.ts) vive no sessionStorage da aba e
 * não expira — então o cliente continuou usando a chave `sb-ver-como-auth-token`
 * com a sessão DO PACIENTE. Resultado: todo /api respondia 403 (token válido,
 * mas não é da equipe) e o portal jogava para /admin/login. Quando a sessão do
 * paciente foi revogada, virou 401 contínuo — e entrar de novo em OUTRA aba não
 * resolvia, porque cada aba lê uma chave de storage diferente e o supabase-js só
 * sincroniza abas com a MESMA storageKey.
 *
 * O conserto: a aba isolada se desfaz sozinha. Ao entrar em qualquer rota da
 * equipe, esta guarda encerra SÓ a sessão desta aba (scope local — global
 * derrubaria o paciente no celular dele), apaga as marcas e recarrega. Depois do
 * reload o client nasce comum e lê de novo a sessão do próprio admin, que nunca
 * saiu do localStorage.
 *
 * No pop-up da ficha (iframe) a sessão vive só em memória e some com o pop-up —
 * mas isso é só no navegador. Achado da auditoria de 24/set na v1.0.54: fechar o
 * pop-up deixava a sessão VIVA no servidor (auth.sessions), e cada abertura
 * empilhava mais uma, com refresh token válido por dias. Agora, ao fechar, esta
 * guarda revoga a sessão desta janela (soltarSessaoIsolada → POST logout com
 * scope=local e keepalive), sem passar pelo client e sem evento no canal — o
 * SIGNED_OUT derrubaria a aba do admin ao lado.
 *
 * Dois caminhos, porque um só não basta: o pai avisa por postMessage antes de
 * tirar o iframe do DOM, e o pagehide cobre o resto (Esc, recarregar a ficha,
 * fechar o navegador). Revoga uma vez só.
 */
import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { isSessaoIsolada, isEmbutido, soltarSessaoIsolada } from '../../lib/supabaseClient';

/** Rotas de quem trabalha na clínica — nenhuma delas é do portal do paciente. */
const ROTAS_DA_EQUIPE = ['/admin', '/doctor', '/nurse'];

export function ehRotaDaEquipe(caminho: string): boolean {
  return ROTAS_DA_EQUIPE.some(r => caminho === r || caminho.startsWith(`${r}/`));
}

/** O pai (PortalDoPacienteModal) manda isto antes de fechar o pop-up. */
export const AVISO_DE_FECHAMENTO = 'essence:ver-como:encerrar';

export default function GuardaDaAbaIsolada() {
  const location = useLocation();
  const desfazendo = useRef(false);
  const jaSoltou = useRef(false);

  // Pop-up da ficha: fechou, a sessão morre TAMBÉM no servidor.
  useEffect(() => {
    if (!isEmbutido()) return;

    const soltarUmaVez = () => {
      if (jaSoltou.current) return;
      jaSoltou.current = true;
      soltarSessaoIsolada();
    };
    const aoReceber = (e: MessageEvent) => {
      if (e.origin !== window.location.origin) return;
      if (e.data?.type === AVISO_DE_FECHAMENTO) soltarUmaVez();
    };

    window.addEventListener('message', aoReceber);
    window.addEventListener('pagehide', soltarUmaVez);
    return () => {
      window.removeEventListener('message', aoReceber);
      window.removeEventListener('pagehide', soltarUmaVez);
    };
  }, []);

  useEffect(() => {
    if (desfazendo.current) return;
    if (isEmbutido()) return;              // pop-up da ficha: morre ao fechar
    if (!isSessaoIsolada()) return;        // aba comum: nada a fazer
    if (!ehRotaDaEquipe(location.pathname)) return;

    desfazendo.current = true;
    // sem signOut: ele dispara SIGNED_OUT no canal da storageKey e derrubaria o
    // pop-up da ficha aberto ao lado (achado da auditoria de 24/set). Apagar as
    // marcas e recarregar basta — a sessão isolada morre com a aba.
    soltarSessaoIsolada();
    window.location.replace(`${location.pathname}${location.search}`);
  }, [location.pathname, location.search]);

  return null;
}
